// src/backendServices/liveStreamManagment/api_microservice_sources.ts
/**
 * Client pour les routes sources du microservice FFmpeg :
 *   /xtream/*        — comptes Xtream, channels, logos, sélection
 *   /hls-streams/*   — flux HLS libres
 *   /rtmp-inputs/*   — caméras RTMP additionnelles de la chaîne
 *   /ptz/*           — caméras PTZ
 *
 * `microserviceUrl` : URL de base du microservice (ex: http://192.168.1.x:8000)
 *                     On n'ajoute PAS de streamPath ici — le microservice est
 *                     propre à un broadcastingChannel, son URL suffit.
 *
 * v2 — updateHlsStream : patch étendu avec champs Xtream
 *      (xtream_stream_id, xtream_stream_icon, xtream_account_id)
 */

import {
  MicroserviceXtreamAccount,
  MicroserviceXtreamChannelsResponse,
  MicroserviceXtreamSelected,
  MicroserviceHlsStream,
  MicroservicePtzCamera,
  MicroserviceRtmpInput,
  MicroserviceRtmpStatus,
  XtreamAccountCreatePayload,
  HlsStreamCreatePayload,
  RtmpInputCreatePayload,
  PtzCameraCreatePayload,
  PtzPosition,
  PtzPreset,
} from '../../dataTypes/liveStreamManagment/types_microservice_sources';

// ── Helper fetch ──────────────────────────────────────────────────────────────

async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return res.json();
}

// ── Type interne pour le dump HLS renvoyé par GET /hls-streams ───────────────
// L'API retourne : { streams: { total, free, xtream, streams: HlsStream[] } }
// car l'endpoint appelle hls_registry.dump() qui renvoie cet objet imbriqué.

interface HlsStreamsDump {
  total   : number;
  free    : number;
  xtream  : number;
  streams : MicroserviceHlsStream[];
}

interface HlsStreamsResponse {
  streams: HlsStreamsDump;
}

// ── Type patch HLS étendu ─────────────────────────────────────────────────────

export interface HlsStreamPatch {
  label?              : string;
  url?                : string;
  // Champs Xtream uniquement — ignorés par le backend pour les flux free
  xtream_stream_id?   : number;
  xtream_stream_icon? : string;
  xtream_account_id?  : string;
}

// ============================================================================
// XTREAM — Comptes
// ============================================================================

export const listXtreamAccounts = (base: string): Promise<{ accounts: MicroserviceXtreamAccount[] }> =>
  apiFetch(`${base}/xtream/accounts`);

export const addXtreamAccount = (
  base   : string,
  payload: XtreamAccountCreatePayload,
): Promise<{ account: MicroserviceXtreamAccount }> =>
  apiFetch(`${base}/xtream/accounts`, {
    method: 'POST',
    body  : JSON.stringify(payload),
  });

export const getXtreamAccount = (
  base          : string,
  accountId     : string,
  includePassword = false,
): Promise<{ account: MicroserviceXtreamAccount & { password?: string } }> =>
  apiFetch(`${base}/xtream/accounts/${accountId}?include_password=${includePassword}`);

export const deleteXtreamAccount = (
  base     : string,
  accountId: string,
): Promise<{ status: string }> =>
  apiFetch(`${base}/xtream/accounts/${accountId}`, { method: 'DELETE' });

// ============================================================================
// XTREAM — Channels
// ============================================================================

export const getXtreamChannels = (
  base     : string,
  accountId: string,
): Promise<MicroserviceXtreamChannelsResponse> =>
  apiFetch(`${base}/xtream/accounts/${accountId}/channels`);

export const refreshXtreamChannels = (
  base     : string,
  accountId: string,
): Promise<MicroserviceXtreamChannelsResponse> =>
  apiFetch(`${base}/xtream/accounts/${accountId}/channels/refresh`, { method: 'POST' });

// ============================================================================
// XTREAM — Logo
// ============================================================================

/**
 * Retourne l'URL du logo servi par le microservice (cache local).
 * Le microservice télécharge le logo à la première demande.
 */
export const getXtreamLogoUrl = (
  base      : string,
  accountId : string,
  streamId  : number,
  logoUrl   : string,
): string => {
  const encoded = encodeURIComponent(logoUrl);
  return `${base}/xtream/accounts/${accountId}/logos/${streamId}?logo_url=${encoded}`;
};

// ============================================================================
// XTREAM — Sélection
// ============================================================================

export const setSelectedXtreamChannel = (
  base     : string,
  accountId: string,
  payload  : { stream_id: number; name: string; stream_icon: string },
): Promise<{ selected: MicroserviceXtreamSelected }> =>
  apiFetch(`${base}/xtream/accounts/${accountId}/selected`, {
    method: 'PUT',
    body  : JSON.stringify(payload),
  });

export const getSelectedXtreamChannel = (
  base     : string,
  accountId: string,
): Promise<{ selected: MicroserviceXtreamSelected | null }> =>
  apiFetch(`${base}/xtream/accounts/${accountId}/selected`);

export const clearSelectedXtreamChannel = (
  base     : string,
  accountId: string,
): Promise<{ status: string }> =>
  apiFetch(`${base}/xtream/accounts/${accountId}/selected`, { method: 'DELETE' });

// ============================================================================
// HLS LIBRES
// ============================================================================

/**
 * Liste tous les flux HLS libres (type=free).
 *
 * NOTE : GET /hls-streams retourne le dump complet du registre :
 *   { streams: { total, free, xtream, streams: HlsStream[] } }
 *
 * On normalise ici pour ne renvoyer que le tableau plat des flux "free",
 * ce qui correspond à ce qu'attendait l'ancien code.
 * Si vous voulez tous les flux (free + xtream), utilisez listAllHlsStreams().
 */
export const listHlsStreams = async (base: string): Promise<{ streams: MicroserviceHlsStream[] }> => {
  const raw = await apiFetch<HlsStreamsResponse>(`${base}/hls-streams`);
  // raw.streams est un HlsStreamsDump — on extrait le tableau interne
  const allStreams: MicroserviceHlsStream[] = raw.streams?.streams ?? [];
  // Filtrer uniquement les flux libres (type=free)
  return { streams: allStreams.filter(s => s.type === 'free') };
};

/**
 * Liste TOUS les flux HLS (libres + Xtream).
 */
export const listAllHlsStreams = async (
  base: string,
): Promise<{ streams: MicroserviceHlsStream[]; total: number; free: number; xtream: number }> => {
  const raw = await apiFetch<HlsStreamsResponse>(`${base}/hls-streams`);
  const dump = raw.streams;
  return {
    streams: dump?.streams ?? [],
    total  : dump?.total   ?? 0,
    free   : dump?.free    ?? 0,
    xtream : dump?.xtream  ?? 0,
  };
};

export const addHlsStream = (
  base   : string,
  payload: HlsStreamCreatePayload,
): Promise<{ stream: MicroserviceHlsStream }> =>
  apiFetch(`${base}/hls-streams`, {
    method: 'POST',
    body  : JSON.stringify(payload),
  });

/**
 * Met à jour un flux HLS.
 *
 * Pour les flux libres (type=free)  : seuls label et url sont pertinents.
 * Pour les flux Xtream (type=xtream): les champs xtream_* permettent de
 *   changer la chaîne au sein du même compte sans recréer l'entrée.
 *   Si le compte change (xtream_account_id différent), préférer une
 *   suppression + recréation côté frontend pour rester cohérent.
 */
export const updateHlsStream = (
  base    : string,
  streamId: string,
  patch   : HlsStreamPatch,
): Promise<{ stream: MicroserviceHlsStream }> =>
  apiFetch(`${base}/hls-streams/${streamId}`, {
    method: 'PATCH',
    body  : JSON.stringify(patch),
  });

export const deleteHlsStream = (
  base    : string,
  streamId: string,
): Promise<{ status: string }> =>
  apiFetch(`${base}/hls-streams/${streamId}`, { method: 'DELETE' });

// ============================================================================
// PTZ
// ============================================================================

export const listPtzCameras = (
  base            : string,
  includePassword = false,
): Promise<{ cameras: MicroservicePtzCamera[] }> =>
  apiFetch(`${base}/ptz?include_password=${includePassword}`);

export const addPtzCamera = (
  base   : string,
  payload: PtzCameraCreatePayload,
): Promise<{ camera: MicroservicePtzCamera }> =>
  apiFetch(`${base}/ptz`, {
    method: 'POST',
    body  : JSON.stringify(payload),
  });

export const updatePtzCamera = (
  base    : string,
  cameraId: string,
  patch   : Partial<PtzCameraCreatePayload>,
): Promise<{ camera: MicroservicePtzCamera }> =>
  apiFetch(`${base}/ptz/${cameraId}`, {
    method: 'PATCH',
    body  : JSON.stringify(patch),
  });

export const deletePtzCamera = (
  base    : string,
  cameraId: string,
): Promise<{ status: string }> =>
  apiFetch(`${base}/ptz/${cameraId}`, { method: 'DELETE' });

export const updatePtzPosition = (
  base    : string,
  cameraId: string,
  position: PtzPosition,
): Promise<{ status: string; position: PtzPosition }> =>
  apiFetch(`${base}/ptz/${cameraId}/position`, {
    method: 'PUT',
    body  : JSON.stringify(position),
  });

export const addPtzPreset = (
  base    : string,
  cameraId: string,
  preset  : PtzPreset,
): Promise<{ status: string }> =>
  apiFetch(`${base}/ptz/${cameraId}/presets`, {
    method: 'POST',
    body  : JSON.stringify({ preset_id: preset.id, preset_label: preset.label }),
  });

export const deletePtzPreset = (
  base    : string,
  cameraId: string,
  presetId: number,
): Promise<{ status: string }> =>
  apiFetch(`${base}/ptz/${cameraId}/presets/${presetId}`, { method: 'DELETE' });
// ============================================================================
// RTMP — caméras additionnelles publiées vers MediaMTX
// ============================================================================
//
// Le microservice tire lui-même la clé `rtmp_{uuid chaîne}{5 caractères}` : on
// ne la lui fournit jamais. C'est lui qui connaît l'UUID de sa chaîne et les
// clés déjà prises, et c'est ce qui garantit qu'une régie ne peut pas
// s'attribuer la caméra d'une autre chaîne.

export const listRtmpInputs = (base: string): Promise<{ inputs: MicroserviceRtmpInput[] }> =>
  apiFetch(`${base}/rtmp-inputs`);

/** `baseUrl` : façade publique de la régie (ex: https://cast.kleonpro.tv). */
export const addRtmpInput = (
  base   : string,
  payload: RtmpInputCreatePayload,
): Promise<{ input: MicroserviceRtmpInput }> =>
  apiFetch(`${base}/rtmp-inputs`, {
    method: 'POST',
    body  : JSON.stringify(payload),
  });

export const renameRtmpInput = (
  base    : string,
  streamId: string,
  label   : string,
): Promise<{ status: string; input: MicroserviceRtmpInput }> =>
  apiFetch(`${base}/rtmp-inputs/${streamId}`, {
    method: 'PATCH',
    body  : JSON.stringify({ label }),
  });

/**
 * Tire de nouveaux login / mot de passe de publication. L'ancienne URL est refusée pour toute
 * NOUVELLE connexion ; un encodeur déjà connecté n'est pas coupé. Erreur (502) = le manager
 * MediaMTX a refusé, rien n'a changé. `baseUrl` : façade publique, pour recomposer l'URL.
 */
export const regenerateRtmpInputCredentials = (
  base    : string,
  streamId: string,
  baseUrl : string,
): Promise<{ status: string; input: MicroserviceRtmpInput }> =>
  apiFetch(`${base}/rtmp-inputs/${streamId}/regenerate-credentials`, {
    method: 'POST',
    body  : JSON.stringify({ base_url: baseUrl }),
  });

export const deleteRtmpInput = (
  base    : string,
  streamId: string,
): Promise<{ status: string; stream_id: string }> =>
  apiFetch(`${base}/rtmp-inputs/${streamId}`, { method: 'DELETE' });

/** `waiting` tant qu'aucun encodeur ne publie : la playlist HLS répond 404 d'ici là. */
export const getRtmpInputStatus = (
  base    : string,
  streamId: string,
): Promise<{ status: MicroserviceRtmpStatus }> =>
  apiFetch(`${base}/rtmp-inputs/${streamId}/status`);
